import React, { useState } from "react";
import {
  ZoomIn,
  Maximize2,
  X,
  ImageOff,
  ExternalLink,
  Loader2,
} from "lucide-react";

export default function ECGImageViewer({ src, label = "ECG" }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  if (!src) {
    return (
      <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] flex flex-col items-center justify-center py-10 text-[#6B7280]">
        <ImageOff className="w-6 h-6 mb-2" />

        <p className="text-sm">
          {label === "ECG"
            ? "ECG image not available"
            : "Report not available"}
        </p>
      </div>
    );
  }

  if (imageError) {
    return (
      <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] flex flex-col items-center justify-center py-8 px-4 text-center">
        <ImageOff className="w-7 h-7 mb-2 text-[#DC2626]" />

        <p className="text-sm font-medium text-[#991B1B]">
          Unable to load {label} image
        </p>

        <p className="text-xs text-[#7F1D1D] mt-1 mb-4">
          The image URL may be unavailable or inaccessible.
        </p>

        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#DC2626] text-white text-sm font-medium hover:bg-[#B91C1C]"
        >
          <ExternalLink className="w-4 h-4" />
          Open {label} Image
        </a>
      </div>
    );
  }

  return (
    <>
      <div className="relative rounded-lg border border-[#E5E7EB] overflow-hidden bg-white">

        {/* Loading */}
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#F9FAFB] z-10">
            <Loader2 className="w-6 h-6 text-[#DC2626] animate-spin" />

            <p className="text-xs text-[#6B7280] mt-2">
              Loading {label} image...
            </p>
          </div>
        )}

        {/* Image */}
        <img
          src={src}
          alt={label}
          className={`w-full max-h-[500px] object-contain cursor-zoom-in transition-opacity ${
            loading ? "opacity-0" : "opacity-100"
          }`}
          onLoad={() => {
            setLoading(false);
            setImageError(false);
          }}
          onError={() => {
            setLoading(false);
            setImageError(true);
          }}
          onClick={() => setFullscreen(true)}
        />

        {/* Fullscreen button */}
        {!loading && (
          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className="absolute top-2 right-2 bg-white/95 hover:bg-white border border-[#E5E7EB] rounded-md p-2 shadow-sm transition"
            aria-label={`Open ${label} fullscreen`}
          >
            <Maximize2 className="w-4 h-4 text-[#1F2937]" />
          </button>
        )}
      </div>

      {/* Open original image */}
      {!loading && (
        <div className="flex justify-end mt-2">
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#DC2626] hover:text-[#B91C1C]"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open image
          </a>
        </div>
      )}

      {/* Fullscreen */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setFullscreen(false)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2"
            onClick={() => setFullscreen(false)}
            aria-label="Close fullscreen"
          >
            <X className="w-7 h-7" />
          </button>

          <img
            src={src}
            alt={label}
            className="max-w-full max-h-[95vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

export function ImageThumbLabel() {
  return <ZoomIn className="w-3.5 h-3.5" />;
}